import React, { useRef, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import { offset, scrollTo, addEvent, removeEvent, height } from '../../utils/dom';
import { translate, prefix } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import s from './Page.scss';

const Page = ({ data, pageData, index, location, setup, pageType, hashLocation = false }) => {
	if (!data || ['page', 'start', '404'].indexOf(pageType) === -1) return null;
	const pageElement = useRef(null);
	const [projects, updateProjects] = useState(10);
	const [currentPageTop, updateCurrentPageTop] = useState(0);

	/* INTERNAL SCROLL */

	const scrollToHash = () => {
		let imagesLoaded = null;
		const localHashLocation = hashLocation || window.location.hash;

		const allLoaded = () => {
			if (pageElement && pageElement.current) {
				const imgs = pageElement.current.querySelectorAll('img');
				imagesLoaded = [...imgs].length;

				[...imgs].forEach((img) => {
					if (img.complete) imagesLoaded -= 1;
				});
			}

			if (pageElement && pageElement.current && imagesLoaded === 0) {
				// Delay to understand page navigation
				setTimeout(() => {
					const position = offset(document.getElementById(localHashLocation.replace('#', ''))).top - 80;
					scrollTo(500, position);
				}, 300);
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	};

	useEffect(() => {
		if (hashLocation || window.location.hash !== '') scrollToHash();

		const scrollEvent = () => {
			const pageTop = window.pageYOffset || document.documentElement.scrollTop;

			if (pageTop + (window.innerHeight * 2) > height(document) && pageTop > currentPageTop) {
				// Setting this only makes it possible to load one project at a time.
				updateCurrentPageTop(pageTop + (window.innerHeight * 2));
				updateProjects((nextState) => nextState + 10);
			}
		};

		addEvent(window, 'scroll.pages', () => {
			window.requestAnimationFrame(scrollEvent);
		});

		return () => {
			removeEvent(window, 'scroll.pages');
		};
	}, [currentPageTop]);

	/* EXTRA PARTS */

	// Check if page contains core/block
	if (data.parts) {
		const removeParts = [];

		data.content.sections.map((section) => {
			if (Object.keys(section)[0] === 'coreBlock') {
				const whichPart = section.coreBlock.content;

				data.parts.forEach((part, i) => {
					if (part.meta.id === whichPart) {
						section.coreBlock.content = deepClone(part.sections);
						removeParts.push(i);
					}
				});

				return section;
			}
			return section;
		});

		removeParts.forEach((rPart) => {
			data.parts = [...data.parts.slice(0, rPart), ...data.parts.slice(rPart + 1)];
		});
	}

	const projectURIs = index.map((project) => project.uri);
	const projectMeta = index.reduce((acc, project) => ({ ...acc, [project.uri]: project }), {});
	let pagePosition = projectURIs.indexOf(data.content.meta.default.uri);
	let allPagesInOrder = pagePosition !== -1 ? [...projectURIs.slice(pagePosition), ...projectURIs.slice(0, pagePosition)] : projectURIs;
	let currentPages = [];

	if (location.type !== 'START') {
		// Only run if empty
		if (Object.keys(projectMeta).length === 0) {
			let extractedProjectMeta = {};
			let extractedEditorialLink = {};
			data.content.sections.forEach((origSection) => {
				const section = deepClone(origSection);
				const template = Object.keys(section)[0];
				if (template === 'acfEditorialLink') extractedEditorialLink = deepClone(section);
				if (template === 'acfProjectMeta') extractedProjectMeta = deepClone(section);
			}, []);

			projectURIs[projectURIs.length] = data.content.meta.default.uri;
			projectMeta[data.content.meta.default.uri] = {
				...data.content.meta.default,
				...extractedProjectMeta,
				...extractedEditorialLink,
			};
			allPagesInOrder = [data.content.meta.default.uri];
			pagePosition = 0;
		}
	}

	for (let i = 0; i < projects; i += 1) {
		if (!pageData[allPagesInOrder[i]]) continue;
		currentPages = [...currentPages, pageData[allPagesInOrder[i]]];
	}

	return (
		<main role="main">
			<Top setup={setup} pageContent={data.content.meta?.[setup.language] || data.content.meta.default} paths={data.paths} locationType={location.type} />
			<div className={[s.page]} ref={pageElement}>
				<div className={[s.index, (location.type !== 'START' ? s.index__projects : null)].join(' ')}>
					{
						currentPages.map((page, i) => {
							const currentProjectMeta = projectMeta[allPagesInOrder[i]].acfProjectMeta;
							const currentEditorialProjects = projectMeta[allPagesInOrder[i]].editorialLinks || [];
							const dates = translate(setup, currentProjectMeta.content.dates);

							return (
								<div key={page.content.meta.default.uri} className={s.page_inner}>
									<div>
										<div className={s.page_inner_meta} style={{ marginBottom: `${(dates.length * 11) + 30}px` }}>
											<div className={s.page_inner_meta_projectNumber}>{ `#${page.content?.meta?.default?.order}` }</div>
											<div className={s.page_inner_meta_projectDates}>{ translate(setup, currentProjectMeta.content.dates) }</div>
										</div>
										<div className={s.page_inner_content}>
											<h1 className={s.page_inner_content_title}>{ translate(setup, page.content?.meta?.default?.title) }</h1>
											<TemplateParser meta={page.content.meta} sections={page.content.sections} setup={setup} />
										</div>
									</div>
									<div className={s.page_inner_footer}>
										{
											currentEditorialProjects.length > 0 && currentEditorialProjects.map((currentEditorialProject) => {
												const currentEditorialProjectMeta = currentEditorialProject?.acfEditorialLink?.content?.link?.posts?.[0].meta?.default || {};
												if (!currentEditorialProject || !currentEditorialProjectMeta.fullUri) return null;
												return (
													<Link key={`editorial_project_${currentEditorialProjectMeta.fullUri}`} className={s.page_inner_footer_link} to={prefix(setup, currentEditorialProjectMeta.fullUri)}>
														<span className={s.page_inner_footer_link_read}>{ translate(setup, 'readMore') }</span>
														<svg className={s.page_inner_footer_link_spacer} width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
															<rect x="0.833008" y="6.31348" width="7.75089" height="7.75089" transform="rotate(-45 0.833008 6.31348)" />
														</svg>
														<div className={s.page_inner_footer_link_inner}>
															<span className={s.page_inner_footer_link_inner_title}>{ currentEditorialProjectMeta.title }</span>
															{
																currentEditorialProjectMeta.author && <span className={s.page_inner_footer_link_inner_author}>{ `${translate(setup, 'by')} ${currentEditorialProjectMeta.author}` }</span>
															}
														</div>
													</Link>
												);
											})
										}
										<div className={s.page_inner_footer_caption}>{ translate(setup, currentProjectMeta.content.credits) }</div>
									</div>
								</div>
							);
						})
					}
				</div>
			</div>
		</main>
	);
};

const mapState = ({ environment, location, pageData }, { data, pageType }) => ({
	data,
	environment,
	hashLocation: environment.hashLocation,
	index: environment.index,
	location,
	pageData,
	pageType,
	setup: environment.setup,
});

export default hot(module)(connect(mapState)(Page));
